import {
  Component,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit
} from "@angular/core";
import { Event, EventListener } from "event-source-polyfill";
import { ServerEventsService } from "src/app/services/server-events.service";
import { SseEvents } from "src/app/sse/sse-events";

type SSEEventHandler<T> = (eventPayload: MessageEvent<T>) => void;

type SSERegisteredHandlers = Array<{ type: string; listener: EventListener }>;

export type SSERegister<T = any> = (
  eventTypes: SseEvents[],
  eventHandler: SSEEventHandler<T>
) => void;

export type SSESetupReturnType = Array<[SseEvents[], SSEEventHandler<any>]>;

@Component({ template: "", standalone: true })
export abstract class SseComponent implements OnInit, OnDestroy {
  @Input()
  disableSse: boolean = false;

  private sseRegisteredHandlersMap: SSERegisteredHandlers = [];

  private readonly serverEventsService = inject(ServerEventsService);
  protected readonly ngZone = inject(NgZone);

  ngOnInit(): void {
    if (!this.disableSse) {
      const setupConfig = this.setupSSEHandlers((eventTypes, eventHandler) => {
        const listenerFunc = (event: Event) => {
          const messageEvent = <MessageEvent>event;
          const messagePayload: MessageEvent = {
            ...messageEvent,
            data: messageEvent.data
              ? JSON.parse(messageEvent.data)
              : messageEvent.data
          };

          this.ngZone.run(() => {
            eventHandler(messagePayload);
          });
        };

        eventTypes.forEach((eventType) => {
          this.sseRegisteredHandlersMap.push({
            type: eventType,
            listener: listenerFunc
          });

          this.serverEventsService.addListener(eventType, listenerFunc);
        });
      });

      if (setupConfig) {
        setupConfig.forEach(([eventTypes, callback]) => {
          const listenerFunc = (event: Event) => {
            const messageEvent = <MessageEvent>event;
            const messagePayload: MessageEvent = {
              ...messageEvent,
              data: messageEvent.data
                ? JSON.parse(messageEvent.data)
                : messageEvent.data
            };

            this.ngZone.run(() => {
              callback(messagePayload);
            });
          };

          eventTypes.forEach((eventType) => {
            this.sseRegisteredHandlersMap.push({
              type: eventType,
              listener: listenerFunc
            });

            this.serverEventsService.addListener(eventType, listenerFunc);
          });
        });
      }
    }
  }

  ngOnDestroy() {
    this.sseRegisteredHandlersMap.forEach((handler) => {
      this.serverEventsService.removeListener(handler.type, handler.listener);
    });
  }

  abstract setupSSEHandlers(
    sseRegister: SSERegister
  ): void | SSESetupReturnType;
}
